import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";

import { getColumnDataKey } from "./filtering";

/**
 * The column set a v1 image feed (`QueryResultTable.tableColumns`) puts on
 * screen in the modal and the viewer.
 *
 * Presentation keys (image URL, keyframe hash, location) never render. When
 * the org has configured its camera image traits, those lead in the configured
 * order and the rest are dropped — the same subset the legacy grid showed — or
 * appended after them for an org that surfaces every trait it has (JBS Brazil,
 * whose onsite-data traits are never in the saved settings).
 */
export const buildImageColumns = (
  columns: readonly MeqColumnDef<MeqRowData>[],
  {
    appendUnlisted = false,
    hiddenColumnNames = [],
    traits,
  }: {
    appendUnlisted?: boolean;
    hiddenColumnNames?: readonly string[];
    traits?: readonly string[];
  } = {},
): MeqColumnDef<MeqRowData>[] => {
  const hidden = new Set(hiddenColumnNames);
  const visible = columns.filter((column) => {
    const key = getColumnDataKey(column);
    return key !== undefined && !hidden.has(key);
  });

  if (traits === undefined || traits.length === 0) return visible;

  const configured = traits.flatMap((trait) => {
    const match = visible.find((column) => getColumnDataKey(column) === trait);
    return match === undefined ? [] : [match];
  });
  if (!appendUnlisted) return configured;

  const listed = new Set(configured);
  return [...configured, ...visible.filter((column) => !listed.has(column))];
};
